import React, { useMemo } from 'react'
import { Skeleton, Empty, Typography } from 'antd'
import { Chart } from 'react-chartjs-2'
import dayjs from 'dayjs'
import { MetricKey, METRICS, TimeGranularity } from './types'
import { ResultSet } from '@cubejs-client/core'

const { Text } = Typography

interface PerformanceChartChartProps {
  resultSet: ResultSet | null
  selectedMetrics: MetricKey[]
  barMetric: MetricKey
  timeGranularity: TimeGranularity
  selectedCampaignIds: string[]
  isLoading: boolean
  error: Error | null
  className?: string
}

export const PerformanceChartChart: React.FC<PerformanceChartChartProps> = ({
  resultSet,
  selectedMetrics,
  barMetric,
  timeGranularity,
  selectedCampaignIds,
  isLoading,
  error,
  className = ''
}) => {
  // Transform data for Chart.js
  const chartData = useMemo(() => {
    if (!resultSet) return null

    const data = resultSet.chartPivot()
    const labels = data.map(row => {
      const date = dayjs(row.x)
      switch (timeGranularity) {
        case 'day':
          return date.format('MMM D')
        case 'week':
          return `Week of ${date.format('MMM D')}`
        case 'month':
          return date.format('MMM YYYY')
        default:
          return date.format('MMM D')
      }
    })

    const datasets = selectedMetrics.map((metricKey) => {
      const metric = METRICS.find(m => m.key === metricKey)!
      const isBar = metricKey === barMetric
      
      return {
        label: metric.label,
        data: data.map(row => row[metric.measure] || 0),
        backgroundColor: isBar ? metric.color : 'transparent',
        borderColor: metric.color,
        borderWidth: isBar ? 0 : 2,
        type: isBar ? 'bar' as const : 'line' as const,
        yAxisID: isBar ? 'y' : 'y1', // All lines use right axis (y1)
        order: isBar ? 2 : 1 // Bars render behind lines
      }
    })

    return { labels, datasets }
  }, [resultSet, selectedMetrics, barMetric, timeGranularity])

  // Chart options
  const chartOptions = useMemo(() => {
    const hasLineCharts = selectedMetrics.some(m => m !== barMetric)
    const lineMetrics = selectedMetrics.filter(m => m !== barMetric)
    const lineMetricsLabels = lineMetrics.map(key => METRICS.find(m => m.key === key)?.label).filter(Boolean).join(', ')
    
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index' as const,
        intersect: false
      },
      plugins: {
        legend: {
          position: 'top' as const,
          labels: {
            usePointStyle: true
          }
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const metric = METRICS.find(m => m.label === context.dataset.label)
              if (!metric) return context.formattedValue
              return `${metric.label}: ${metric.formatter(context.parsed.y)}`
            }
          }
        }
      },
      scales: {
        x: {
          grid: {
            display: false
          }
        },
        y: {
          type: 'linear' as const,
          display: true,
          position: 'left' as const,
          grid: {
            color: 'rgba(0, 0, 0, 0.05)'
          },
          title: {
            display: true,
            text: METRICS.find(m => m.key === barMetric)?.label || 'Value'
          }
        },
        ...(hasLineCharts ? {
          y1: {
            type: 'linear' as const,
            display: true,
            position: 'right' as const,
            grid: {
              drawOnChartArea: false
            },
            title: {
              display: true,
              text: lineMetricsLabels || 'Line Metrics'
            }
          }
        } : {})
      }
    }
  }, [selectedMetrics, barMetric])

  if (isLoading) {
    return (
      <div className="h-[400px] w-full">
        <Skeleton active className="w-full h-full" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="h-[400px] w-full flex items-center justify-center">
        <Text type="danger">Error loading data: {error.message}</Text>
      </div>
    )
  }

  if (!chartData || selectedCampaignIds.length === 0) {
    return (
      <div className="h-[400px] w-full flex items-center justify-center">
        <Empty
          description={
            selectedCampaignIds.length === 0 
              ? "Select campaigns to view performance data"
              : "No data available for selected criteria"
          }
        />
      </div>
    )
  }

  return (
    <div className={`h-[400px] ${className}`}>
      <Chart type='bar' data={chartData} options={chartOptions} />
    </div>
  )
}
